/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-bitwise */
import { HandType } from '@ps/web-poker-types';

import type { AttributeKey } from './formatters/types';

export enum TournamentState {
  'ANNOUNCED' = 'ANNOUNCED',
  'CANCELLED' = 'CANCELLED',
  'COMPLETED' = 'COMPLETED',
  'FLIGHTED' = 'FLIGHTED',
  'FULL' = 'FULL',
  'LATE_REGISTRATION' = 'LATE_REGISTRATION',
  'PAUSED' = 'PAUSED',
  'PHASE' = 'PHASE',
  'REGISTERING' = 'REGISTERING',
  'RESTRICTED' = 'RESTRICTED',
  'RUNNING' = 'RUNNING',
  'SEATING' = 'SEATING'
}

export interface PokerGameTileAttribute {
  name: string;
  value: string;
  moneyIcon?: string;
  key: AttributeKey;
}

export interface PokerGameTileTag {
  title: string;
  color: string;
}

export interface PokerGameTile {
  attributes: PokerGameTileAttribute[];
  iconUrl: string;
  id: string;
  clubId: number;
  imageUrl: string;
  imageUrlV2?: string;
  fallbackImageUrl?: string;
  lastEntry: string;
  link: string;
  tags: PokerGameTileTag[];
  type: string;
  server: string;
  serverObj: string;
  ePoolBlockGameType?: number;
  isPlayMoney?: boolean;
  mobileName?: string;
  tournGroup?: number;
  hasVideoSupport?: boolean;
  tournamentState?: TournamentState;
}

export enum Languages {
  en,
  'en-GB',
  'en-US',
  'en-IN',
  pl,
  nl,
  ja,
  it,
  fr,
  cs,
  et,
  bg,
  da,
  de,
  es,
  fi,
  hr,
  hu,
  ko,
  lt,
  lv,
  no,
  pt,
  ro,
  ru,
  sl,
  sv,
  uk,
  el,
  cz,
  'pt-br',
  'es-419',
  'zh-cn',
  'zh-tw',
  sk
}

export type Language = keyof typeof Languages;

export interface Winners {
  amount: number;
  flag: number;
}

export interface Prizes {
  min: number;
  max: number;
  numTickets: number;
  winners: Winners[];
}

export enum GameCategoryTypes {
  None = 0,
  Cash = 1 << 0,
  MTT = 1 << 1,
  SNG = 1 << 2,
  SAG = 1 << 3,
  Zoom = 1 << 4,
  SFN = 1 << 5,
  Table = 1 << 6, // Currently only used for MSG_MLOBBYNG_GET_GAME_INFO
  PPP = 1 << 7,
  Last = GameCategoryTypes.PPP
}

export interface ServerResponseAttributes {
  // @ts-ignore TODO: add proper typings
  buyIn;
  // @ts-ignore TODO: add proper typings
  rake;
  // @ts-ignore TODO: add proper typings
  currency;
  // @ts-ignore TODO: add proper typings
  maxPlayers;
  // @ts-ignore TODO: add proper typings
  isPlayMoney;
  // @ts-ignore TODO: add proper typings
  scalePM;
  prizes: Prizes[];
}

export interface MappedRawData {
  tourns: PokerGameTile[];
  tableTypes: PokerGameTile[];
  sngTypes: PokerGameTile[];
  sagTypes: PokerGameTile[];
  zoomTypes: PokerGameTile[];
  seatFinders: PokerGameTile[];
  pppTypes: PokerGameTile[];
}

export interface PlayerPoolBlock {
  gameType: number;
  blockedUntil: number;
  activeInfractionsNum: number;
}

// taken from server side collection ePoolBlockGameType
export enum ePoolBlockGameTypeCollection {
  tableType = 1,
  seatFinder = 2,
  zoomType = 3
}

export interface BuyInTier {
  buyInTierId: number;
  buyIn: number;
  rake: number;
  potentialMaxKOWinToDisplay: number;
}

export interface Options {
  language: Language;
  disabledGameTypes: GameCategoryTypes[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hspFlagsMap: any;
  isPPPRevampNeymar: boolean;
  timeZone?: string;
}

export interface GameTypeUrl {
  holdem: string;
  flash: string;
  omaha: string;
  max: string;
  'holdem-six': string;
}

export interface SagGame {
  id: number;
  scriptId: number;
  game: string;
  isUltra: boolean;
  imageUrl: GameTypeUrl;
  isFlash: boolean;
  isMax: boolean;
  isHoldemSix?: boolean;
  buyIn: string;
  players: string;
  winUpTo: string;
  promoRef: string;
  payoutAmount?: string;
  payoutAmountSelected?: string;
  tournSites?: number;
  isEnabled?: boolean;
}

export interface getStakeProps {
  buyIn: number;
  currency: number;
  maxWinnerPayout: number;
  promoRef: string;
  rake: number;
  scalePM: number;
  isPlayMoney: boolean;
}

export const legacyHandTypeToAssetNameMap: Record<HandType, string> = {
  [HandType.NONE]: 'None',
  [HandType.BADUGI]: 'Badugi',
  [HandType.HOLDEM]: 'Holdem',
  [HandType.OMAHA_5]: '5 Omaha',
  [HandType.OMAHA]: 'Omaha',
  [HandType.FIVE_CARD_DRAW]: 'Draw',
  [HandType.SINGLE_DRAW_2_7]: 'Single Draw 27',
  [HandType.STUD]: 'Stud',
  [HandType.RAZZ]: 'Razz',
  [HandType.TRIPLE_DRAW_2_7]: '3 Draw 27',
  [HandType.TRIPLE_DRAW_A_5]: '3 Draw A5',
  [HandType.COURCHEVEL]: 'Courchevel',
  [HandType.OMAHA_6]: '6 Omaha',
  [HandType.SPLIT_HOLDEM]: 'Split Holdem',
  [HandType.SPLIT_OMAHA]: 'Split Omaha',
  [HandType.SHOWTIME_HOLDEM]: 'Showtime Holdem',
  [HandType.SHOWTIME_OMAHA]: 'Showtime Omaha',
  [HandType.FUSION]: 'Fusion',
  [HandType.UNFOLD_CLASSIC]: 'Unfold Classic',
  [HandType.UNFOLD_MAX]: 'Unfold Max',
  [HandType.SIX_PLUS_HOLDEM]: '6+ Holdem',
  [HandType.DEEPWATER_HOLDEM]: 'Deep Water Holdem',
  [HandType.DEEPWATER_OMAHA]: 'Deep Water Omaha',
  [HandType.SWAP_HOLDEM]: 'Swap Holdem',
  [HandType.TEMPEST_HOLDEM]: 'Tempest Holdem',
  [HandType.TURMOIL_HOLDEM]: '', // undefined
  [HandType.HOSE]: 'Hose',
  [HandType.HORSE]: 'Horse',
  [HandType.EIGHT_GAME]: 'Nl 8-Game',
  [HandType.MIXED_HOLDEM]: 'Mixed Holdem',
  [HandType.MIXED_OMAHA]: 'Mixed Omaha Hl',
  [HandType.POT_LIMIT_HOLDEM_OMAHA]: 'Pl Holdem Pl Omaha',
  [HandType.TRIPLE_STUD]: 'Nl Triple Stud',
  [HandType.NO_LIMIT_HOLDEM_OMAHA]: 'Nl Holdem Pl Omaha',
  [HandType.OMAHA_MIXED]: 'Nl Mixed Omaha'
};
